import _ from 'lodash';
import axios from '../requester';
import { API } from '../constants/api-urls';
import {
  ALLOWED_EMAILS_DOMAINS_MODIFY_FF_QS,
  SETTING_NAMES,
  SETTINGS_NAMESPACES,
  TRACKING
} from 'common/constants/constants';
import * as TrackingService from './tracking.service';
import SnackBarMessage from '../../components/Message';
import React from 'react';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { getSessionModifyFF } from './init.service';
import { convertBoolStrToBool } from '../utils/utils';
import { createGetSettingsMethods } from './settings.service.utils';

export const DefinitionLevels = {
  ORGANIZATION: 'organization',
  TEAM: 'team',
  REPOSITORY: 'repository'
};

export const SettingValues = {
  PM_IMPORT_STRATEGY: 'pmImportStrategy',
  ITERATION_STRATEGY: 'iterationStrategy',
  DEFAULT_BOARD: 'defaultBoard',
  PERFORMANCE_ENABLED: 'performanceEnabled',
  MERGED_USERS_ENABLED: 'showMergedUsers',
  NEW_WORK_SMALL_TOP_RANGE: 'newWorkSmallTopRange',
  NEW_WORK_MEDIUM_TOP_RANGE: 'newWorkMediumTopRange',
  CODE_CHANGES_SMALL_TOP_RANGE: 'codeChangesSmallTopRange',
  CODE_CHANGES_MEDIUM_TOP_RANGE: 'codeChangesMediumTopRange',
  HIGH_RISK_THRESHOLDS: 'workAtRiskNotification',
  LONG_LIVING_THRESHOLDS: 'reviewRequestHangingNotification',
  SUPERFICIAL_REVIEW_THRESHOLDS: 'mergedWithBasicReviewNotification',
  MERGED_WITHOUT_REVIEW_THRESHOLDS: 'mergedWithoutReviewNotification',
  WORKING_DAYS: 'workingDays',
  DAILY_TIME: 'dailyTime',
  MTTR_CONFIG: 'mttrConfig',
  CFR_CONFIG: 'cfrConfig',
  GIT_SELECTED_SYNC_TEAMS: 'gitSelectedSyncTeams',
  HIDE_NON_WORKING_DAY: 'hideNonWorkingDays',
  RA_COST: 'raCost',
  RA_INPUTS: 'raInputs'
};

export const getAllSettings = async () =>
  axios.get(`${API.ORG_SETTINGS_PATH}/search`);

/**
 * Fetches a single setting value directly from the server without loading all settings to Redux.
 * Use this function ONLY when settings are not yet loaded to Redux (usually before onboarding).
 * For all other cases, use the settings from Redux store via useSettingsService hook.
 * @param {string} settingName - The name of the setting to fetch
 * @returns {Promise<any>} The setting value or null if error occurs
 */
export const getAdHocSettingOrDefinitionValue = async settingName => {
  try {
    const response = await getAllSettings();
    const settings = response.data || response;

    // Reuse the existing getSettingOrDefinitionValue logic
    const { getSettingOrDefinitionValue } = createGetSettingsMethods(settings);
    return getSettingOrDefinitionValue({ name: settingName });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch setting ${settingName}:`, error);
    return null;
  }
};

export const getTeamSettings = (teamId, do_not_inherit = []) =>
  axios.get(API.ORG_SETTINGS_PATH, {
    params: {
      team_id: teamId,
      restrict_to_definition_level: true,
      ...(do_not_inherit.length ? { do_not_inherit } : {})
    }
  });

export const updateTeamSettings = async (teamId, settings) =>
  axios.post(API.ORG_SETTINGS_PATH, settings, { params: { team_id: teamId } });

export const addSetting = async ({ name, value, params }) =>
  axios.post(API.ORG_SETTINGS_PATH, { [name]: value }, { params });

export const createSettingsService = (
  settings,
  updateSetting,
  updateSettingsReducer,
  account,
  toggleSnackbar
) => {
  const mergeSettingsWithSessionFFs = settings => {
    const isQsModifingAllowedFF = getSettingOrDefinitionValue({
      name: FEATURE_FLAGS_NAMES.getFFValueFromQS
    });
    const isAccountAllowedModifyFF = email =>
      _.some(ALLOWED_EMAILS_DOMAINS_MODIFY_FF_QS, allowedEmailDomain =>
        email.includes(allowedEmailDomain)
      );
    const accountEmail = _.get(account, 'email', '').toLowerCase();

    if (!isQsModifingAllowedFF || !isAccountAllowedModifyFF(accountEmail)) {
      return settings;
    }
    const sessionFFs = getSessionModifyFF();
    return settings.map(setting => {
      if (setting.namespace === SETTINGS_NAMESPACES.FEATURE_FLAG) {
        const currSessionFF = sessionFFs[setting.name.toLowerCase()];
        if (currSessionFF) {
          const ffBoolValue = convertBoolStrToBool(currSessionFF); // is string of "true" || "false"
          return { ...setting, value: ffBoolValue, defaultValue: ffBoolValue };
        }
      }
      return setting;
    });
  };
  const loadSettings = async () => {
    const allSettings = await getAllSettings();
    await updateSettingsReducer(mergeSettingsWithSessionFFs(allSettings));
  };
  const onUpdateSetting = async (
    settingName,
    newValue,
    entityType,
    entityId,
    showSnackBar
  ) => {
    const pageTab = entityType === 'repository' ? 'Repositories' : 'Configure';

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: pageTab,
      click_type: TRACKING.CLICK.SAVE,
      setting_name: settingName,
      setting_value: newValue
    });
    const params =
      entityId && entityType ? { [`${entityType}_id`]: entityId } : {};
    let variant = 'success';
    let settingUpdateResult;
    let errorMessage;

    try {
      settingUpdateResult = await updateSetting({
        name: settingName,
        value: newValue,
        params
      });
      await loadSettings();
    } catch (error) {
      variant = 'error';
      const responseMessage = _.get(error, 'response.data.message');
      const regexErrorMessage = 'Given pattern is an invalid regex';
      errorMessage = _.startsWith(responseMessage, regexErrorMessage)
        ? regexErrorMessage
        : null;
    }

    const resultMessage =
      settingUpdateResult && settingUpdateResult[settingName];
    const defaultMessage =
      variant === 'success'
        ? 'Settings updated successfully'
        : errorMessage
        ? errorMessage
        : 'Failed to update settings, please contact support';
    const message = resultMessage ? resultMessage : defaultMessage;

    showSnackBar &&
      toggleSnackbar({
        message: <SnackBarMessage message={message} />,
        variant
      });
  };
  const {
    getSettings,
    getSettingDefinition,
    getSettingOrDefinitionValue,
    getTeamOrOrgSetting,
    getSettingsByDefinitionLevel,
    getSetting
  } = createGetSettingsMethods(settings);
  return {
    getSettings,
    getSettingDefinition,
    getSettingOrDefinitionValue,
    getTeamOrOrgSetting,
    getSettingsByDefinitionLevel,
    updateSetting: onUpdateSetting,
    loadSettings,
    getSetting
  };
};

/* istanbul ignore next */
export const filterAndFormatTeamSettings = (notifications, teamId) => ({
  slackChannel: _.get(
    notifications,
    `${SETTING_NAMES.TEAM_SLACK_CHANNEL}.value`,
    null
  ),
  msTeamsChannel: _.get(
    notifications,
    `${SETTING_NAMES.TEAM_MSTEAMS_CHANNEL}.value`,
    null
  ),
  isNotificationEnabled: _.get(
    notifications,
    'teamIsNotificationsEnabled.value',
    false
  ),
  teamId,
  notifications: _.values(_.orderBy(notifications, 'id'))
    .filter(
      ({ value }) =>
        _.isObject(value) &&
        _.findIndex(_.keys(value), key => key === 'enabled') >= 0
    )
    .map(
      ({
        id,
        value,
        name: notificationKey,
        label: name,
        defaultValue,
        customOptions
      }) => ({
        id,
        name,
        enabled: value.enabled,
        limits: customOptions,
        notificationKey,
        values: _.toPairs(_.omit(value, 'enabled')).map(val => ({
          type: val[0],
          value: val[1]
        })),
        defaultValue: _.omit(defaultValue, 'enabled')
      })
    )
});
